// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import flatpickr, { Options as FlatpickrOptions } from 'flatpickr'
import { Polish } from 'flatpickr/dist/l10n/pl'
import React, { useEffect, useRef, useState } from 'react'
import { BiCheck } from 'react-icons/bi'

import { DateString } from '../../../api/services/orders/types.ts'
import { UserCode } from '../../../api/services/user_assignment_codes/types.ts'
import { useAddCodeFormik } from '../../../hooks/useAddCodeFormik.ts'
import { useGetPricingForUser } from '../../../hooks/useGetPricingForUser.ts'
import {
  calculateDaysBetweenDates,
  convertDateStringToFullDate,
  getLatestDate
} from '../../../utils/dates.ts'
import { convertToShortDate } from '../../../utils/formatter.ts'
import Button from '../../common/Button/Button.tsx'
import Input from '../../common/forms/Input/Input.tsx'
import CodePricingItem from '../codePricingItem/CodePricingItem.tsx'

const CodeDetails = ({
  codeObject,
  setChangeStatus,
  setIsPopupVisible,
  mainCodeId
}: {
  codeObject: UserCode | null
  setChangeStatus: React.Dispatch<React.SetStateAction<boolean>>
  setIsPopupVisible: React.Dispatch<React.SetStateAction<boolean>>
  mainCodeId: number
}) => {
  const [collapsePricing, setCollapsePricing] = useState(true)
  const currentDay = new Date()
  const currentDate = convertToShortDate(currentDay.toString()) as DateString
  const tomorrow = new Date(currentDay)
  tomorrow.setDate(currentDay.getDate() + 1)
  const tomorrowShortDate = convertToShortDate(
    tomorrow.toString()
  ) as DateString

  const { userPrices, loading } = useGetPricingForUser()
  const formik = useAddCodeFormik(
    codeObject,
    setChangeStatus,
    setIsPopupVisible,
    collapsePricing,
    userPrices
  )

  const isActiveCode =
    codeObject && codeObject.valid_until
      ? calculateDaysBetweenDates(
          convertDateStringToFullDate(
            formik.initialValues.valid_until as string
          ),
          convertDateStringToFullDate(tomorrowShortDate)
        ) <= 0
      : true

  const [activate, setActivate] = useState(codeObject === null || isActiveCode)

  const validFromRef = useRef<HTMLInputElement>(null)
  const validUntilRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const specialPricongExists =
      codeObject !== null && codeObject.special_pricing !== null
    !loading && setCollapsePricing(!specialPricongExists)
  }, [loading])

  useEffect(() => {
    if (validFromRef.current) {
      const options: FlatpickrOptions = {
        onChange: async (selectedDates: Date[]) => {
          await formik.setFieldValue(
            'valid_from',
            convertToShortDate(selectedDates[0].toString()) as DateString
          )
        },
        locale: Polish,
        mode: 'single',
        static: true,
        monthSelectorType: 'static',
        dateFormat: 'Y-m-d',
        minDate: codeObject ? formik.initialValues.valid_from : currentDate,
        defaultDate: codeObject
          ? formik.initialValues.valid_from
          : formik.values.valid_from,
        prevArrow:
          '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
        nextArrow:
          '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>'
      }

      flatpickr(validFromRef.current, options)
    }

    if (validUntilRef.current) {
      const options: FlatpickrOptions = {
        onChange: async (selectedDates: Date[]) => {
          await formik.setFieldValue(
            'valid_until',
            selectedDates.length
              ? (convertToShortDate(selectedDates[0].toString()) as DateString)
              : null
          )
        },
        locale: Polish,
        mode: 'single',
        static: true,
        monthSelectorType: 'static',
        dateFormat: 'Y-m-d',
        minDate: tomorrowShortDate,
        defaultDate:
          formik.values.valid_until && isActiveCode
            ? getLatestDate(formik.values.valid_until, tomorrowShortDate)
            : null,
        prevArrow:
          '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
        nextArrow:
          '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>'
      }

      flatpickr(validUntilRef.current, options)
    }
  }, [formik.values, validFromRef, validUntilRef])

  const activateHandler = () => {
    formik.setFieldValue('valid_until', null)
    setActivate(true)
  }

  const onClickPricingToggle = () => {
    formik.dirty = true
    formik.setFieldValue('special_pricing', !collapsePricing)
    setCollapsePricing(!collapsePricing)
  }

  return (
    <div className={'sm:min-w-[600px]'}>
      <form onSubmit={formik.handleSubmit}>
        <div className="mb-4">
          <Input
            id="code"
            name="code"
            type="code"
            placeholder="Nazwa"
            formik={formik}
          />
        </div>
        <div className="mb-6">
          <Input
            id="description"
            name="description"
            type="description"
            placeholder="Krótki opis"
            formik={formik}
          />
        </div>

        <div className="mb-6">
          <label htmlFor="valid_from">Data ważności od:</label>
          <input
            id="valid_from"
            name="valid_from"
            ref={validFromRef}
            className={`form-datepicker w-full rounded border-[1.5px] px-5 py-3 font-normal outline-none transition
                          ${codeObject !== null ? 'cursor-not-allowed border-gray-400 bg-gray-100 dark:border-gray-600 dark:bg-gray-600' : 'border-stroke bg-transparent focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary'}
                        `}
            placeholder="Data ważności od:"
            data-class="flatpickr-right"
            value={formik.initialValues.valid_from}
            disabled={codeObject !== null}
          />
          {formik.errors.valid_from && formik.touched.valid_from && (
            <div className="text-red-500">{formik.errors.valid_from}</div>
          )}
        </div>

        {mainCodeId !== codeObject?.id && (
          <div className="mb-6">
            {!isActiveCode && !activate && (
              <div className="flex flex-row gap-4 items-center justify-center w-full">
                <p className="text-red-500 font-bold text-xl">
                  {'Kod dziś wygasa lub jest nie aktywny!'}
                </p>
                <button
                  className="bg-green-500 text-white font-bold border-2 rounded-xl px-4 py-2"
                  onClick={activateHandler}
                >
                  Aktywuj
                </button>
              </div>
            )}
            <div
              className={
                isActiveCode || (!isActiveCode && activate) ? 'block' : 'hidden'
              }
            >
              <label htmlFor="valid_until">Data ważności do:</label>
              <input
                id="valid_until"
                name="valid_until"
                ref={validUntilRef}
                className={
                  'form-datepicker w-full rounded border-[1.5px] border-stroke bg-transparent px-5 py-3 font-normal outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary'
                }
                placeholder="Data ważności do:"
                data-class="flatpickr-right"
              />
            </div>
            {formik.errors.valid_until && formik.touched.valid_until && (
              <div className="text-red-500">{formik.errors.valid_until}</div>
            )}
          </div>
        )}

        <div className="mb-6 border-t-4 border-gray-200 pt-1 dark:border-gray-700">
          <div
            className="flex justify-center items-center cursor-pointer gap-4"
            onClick={onClickPricingToggle}
          >
            <label
              htmlFor="valid_from"
              className="flex justify-center items-center font-bold text-xl my-4"
            >
              Ustaw cennik do kodu
            </label>
            <div className="flex justify-center items-center w-[30px] h-[30px] border-4">
              {!collapsePricing && <BiCheck size={24} />}
            </div>
          </div>
          {!collapsePricing && (
            <>
              <span className="hidden 3xl:block text-center">
                Ceny z domyślnego cennika
              </span>
              <div className="flex flex-col 3xl:flex-row justify-center items-center gap-1 mt-4">
                <CodePricingItem
                  formik={formik}
                  fieldName={'month_diet_price'}
                  placeholder={'Cena za 30 dni'}
                  userPrices={userPrices}
                />
                <CodePricingItem
                  formik={formik}
                  fieldName={'three_month_diet_price'}
                  placeholder={'Cena za 90 dni'}
                  userPrices={userPrices}
                />
                <CodePricingItem
                  formik={formik}
                  fieldName={'six_month_diet_price'}
                  placeholder={'Cena za 180 dni'}
                  userPrices={userPrices}
                />
                <CodePricingItem
                  formik={formik}
                  fieldName={'twelve_month_diet_price'}
                  placeholder={'Cena za 360 dni'}
                  userPrices={userPrices}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="filled"
            fullWidth
            size="medium"
            color="primary"
            type="submit"
            className="mb-3"
            disabled={formik.isSubmitting || !formik.dirty}
          >
            {formik.isSubmitting
              ? 'Zapisywanie...'
              : codeObject
                ? 'Zapisz zmiany'
                : 'Utwórz nowy kod'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CodeDetails
